/**
 * Hoja de Tricount (otro libro, el compartido con la pareja). Mismo formato y reglas que apps/shared/TricountStore.js:
 * - Pestaña "Tricount", títulos en la fila 5 y datos desde la 6 en B:F (Fecha, Concepto, Cantidad, PagadoPor, Origen).
 * - Un gasto con la misma Fecha y Cantidad que otro ya apuntado va a "PosiblesDuplicados" (mismo formato).
 * - C2 = total pagado por Pilar, C3 = total pagado por Raul; se reparte al 50 %.
 */
import { num, serialToIso, text } from './cells.js';
import type { SheetsApi } from './write.js';

export const TRICOUNT = {
  main: 'Tricount', dup: 'PosiblesDuplicados', removed: 'Quitados', history: 'Histórico', settlements: 'Saldos', firstRow: 6,
  payers: ['Pilar', 'Raul'] as const, origins: ['Tickets', 'Cuenta', 'Efectivo'] as const, maxAmount: 5000,
};
export type TricountOrigin = (typeof TRICOUNT.origins)[number];

export interface TricountExpense { date: string; concept: string; amount: number; payer: string; origin: TricountOrigin }

/** Concepto como en la web antigua: "nota - concepto" (o el que haya), sin caracteres de control ni fórmulas. */
export function tricountConcept(note: string, concept: string): string {
  const n = note.trim(), c = concept.trim();
  return cleanConcept(n && c ? `${n} - ${c}` : n || c);
}

export const cleanConcept = (s: string) => s.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().replace(/^[=+\-@]+/, '').trim().slice(0, 120);

/** Origen de Tricount según la cuenta del movimiento. */
export function tricountOrigin(account: string): TricountOrigin {
  const a = account.toLowerCase();
  return a.includes('ticket') || a.includes('pluxee') ? 'Tickets' : a.includes('efectivo') ? 'Efectivo' : 'Cuenta';
}

/** Fecha de una celda de Tricount (número de serie, 'dd/mm/aaaa' o 'aaaa-mm-dd') → 'aaaa-mm-dd'. */
function cellDate(v: unknown): string {
  if (typeof v === 'number') return serialToIso(v);
  const s = text(v);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`;
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

const key = (date: string, amount: number) => `${date}|${amount.toFixed(2)}`;

export function validateExpense(e: TricountExpense): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(e.date)) return 'Fecha no válida';
  if (!cleanConcept(e.concept)) return 'Falta el concepto';
  if (!(e.amount > 0) || e.amount > TRICOUNT.maxAmount) return 'Importe no válido';
  if (!TRICOUNT.payers.includes(e.payer as (typeof TRICOUNT.payers)[number])) return 'Quién paga no es válido';
  if (!TRICOUNT.origins.includes(e.origin)) return 'Origen no válido';
  return '';
}

/**
 * Apunta un gasto en Tricount. Idempotente: si ya hay una fila idéntica (en la principal o en duplicados), no escribe.
 * Devuelve dónde quedó: 'main', 'dup' o 'already'.
 */
export async function addTricountExpense(api: SheetsApi, e: TricountExpense): Promise<'main' | 'dup' | 'already'> {
  const err = validateExpense(e);
  if (err) throw new Error(err);
  const concept = cleanConcept(e.concept);
  const amount = Math.round(e.amount * 100) / 100;
  const read = async (tab: string) => (await api.get(`${tab}!B${TRICOUNT.firstRow}:F`)).map(r => ({ filled: text(r?.[0]) !== '', date: cellDate(r?.[0]), concept: text(r?.[1]), amount: num(r?.[2]), payer: text(r?.[3]), origin: text(r?.[4]) }));
  const [main, dup] = await Promise.all([read(TRICOUNT.main), read(TRICOUNT.dup)]);
  const same = (x: (typeof main)[number]) => x.date === e.date && x.concept === concept && x.amount === amount && x.payer === e.payer && x.origin === e.origin;
  if (main.some(same) || dup.some(same)) return 'already';
  const isDup = main.some(x => x.date && x.amount !== null && key(x.date, x.amount) === key(e.date, amount));
  const rows = isDup ? dup : main;
  const tab = isDup ? TRICOUNT.dup : TRICOUNT.main;
  // Primera fila libre tras la última con Fecha (columna B), como la app antigua: lo que haya más abajo en otras
  // columnas (casillas, fórmulas arrastradas…) no cuenta
  let last = -1;
  rows.forEach((x, i) => { if (x.filled) last = i; });
  const row = TRICOUNT.firstRow + last + 1;
  // Una sola escritura "como si se tecleara" (la fecha queda como fecha, igual que el setValues de Apps Script).
  // El apóstrofo inicial obliga a que el concepto sea texto: nunca una fórmula, un número ni una fecha.
  await api.update([{ range: `${tab}!B${row}:F${row}`, values: [[e.date, `'${concept}`, amount, e.payer, e.origin]] }], 'USER_ENTERED');
  return isDup ? 'dup' : 'main';
}

/** Una fila del libro de Tricount (fila = número de fila en la hoja). */
export interface TricountRow { date: string; concept: string; amount: number; payer: string; origin: string; row: number }

/** Filas con fecha e importe de una pestaña (B:F desde la fila 6). */
export function parseTricountRows(values: unknown[][]): TricountRow[] {
  return values.flatMap((r, i) => {
    const date = cellDate(r?.[0]), amount = num(r?.[2]);
    return date && amount !== null ? [{ date, concept: text(r?.[1]), amount, payer: text(r?.[3]), origin: text(r?.[4]), row: TRICOUNT.firstRow + i }] : [];
  });
}

/** Lee el libro entero: la pestaña principal (lo que cuenta en el saldo) y los posibles duplicados. Solo lectura. */
export async function readTricount(api: SheetsApi): Promise<{ rows: TricountRow[]; duplicates: TricountRow[] }> {
  const [main, dup] = await Promise.all([api.get(`${TRICOUNT.main}!B${TRICOUNT.firstRow}:F`), api.get(`${TRICOUNT.dup}!B${TRICOUNT.firstRow}:F`).catch(() => [])]);
  return { rows: parseTricountRows(main), duplicates: parseTricountRows(dup) };
}

/** Un gasto tal como está en Tricount (para quitarlo: se busca por su contenido, no por el número de fila). */
export interface TricountEntry { date: string; concept: string; amount: number; payer: string; origin: string }
const QUITADOS_HEAD = ['Fecha', 'Concepto', 'Cantidad', 'PagadoPor', 'Origen', 'Quitado el', 'Quitado por', 'Operación', 'Iguales antes'];

/**
 * Quita un gasto de Tricount sin perderlo: primero lo copia a la pestaña «Quitados» (con fecha, quién y la operación),
 * luego borra su fila. Seguro al reintentar: la copia guarda cuántas filas iguales había; si ya hay menos, ya se borró
 * (y así nunca se borra otra fila idéntica). Devuelve 'removed' o 'missing' (no estaba).
 */
export async function removeTricountExpense(api: SheetsApi, e: TricountEntry, op: { id: string; at: string; by: string }): Promise<'removed' | 'missing'> {
  const same = (x: TricountRow) => x.date === e.date && x.concept === e.concept && Math.abs(x.amount - e.amount) < 0.005 && x.payer === e.payer && x.origin === e.origin;
  const main = parseTricountRows(await api.get(`${TRICOUNT.main}!B${TRICOUNT.firstRow}:F`));
  const hits = main.filter(same);
  let q = await api.get(`${TRICOUNT.removed}!A1:I`).catch(() => null);
  const copy = q?.find(r => text(r?.[7]) === op.id);
  if (copy) {
    // Reintento: ya se copió; solo se borra si siguen quedando tantas iguales como antes
    if (hits.length < (num(copy[8]) ?? 1) || !hits.length) return 'removed';
  } else {
    if (!hits.length) return 'missing';
    if (!q) await api.addSheet(TRICOUNT.removed);
    if (!q?.length) { await api.update([{ range: `${TRICOUNT.removed}!A1:I1`, values: [QUITADOS_HEAD] }]); q = [QUITADOS_HEAD]; }
    const row = q.length + 1;
    await api.update([{ range: `${TRICOUNT.removed}!A${row}:I${row}`, values: [[e.date, `'${cleanConcept(e.concept)}`, e.amount, e.payer, e.origin, op.at, op.by, op.id, hits.length]] }], 'USER_ENTERED');
  }
  await api.deleteRows([{ tab: TRICOUNT.main, row: hits[hits.length - 1]!.row }]);
  return 'removed';
}

/* ---------- Saldar ---------- */

const HIST_HEAD = ['Fecha', 'Concepto', 'Cantidad', 'PagadoPor', 'Origen', 'Saldado el', 'Operación'];
const SALDOS_HEAD = ['Fecha', 'Paga', 'Recibe', 'Importe', 'Desde', 'Hasta', 'Gastos', 'Total en común', 'Operación', 'Movimiento', 'Notas'];

/** Un saldo entre los dos: quién paga a quién, cuánto y el periodo que cierra. */
export interface Settlement { id: string; date: string; payer: string; receiver: string; amount: number; since: string; until: string; count: number; total: number; movementId: string; note: string; row: number }

async function ensureTab(api: SheetsApi, tab: string, head: string[]): Promise<unknown[][]> {
  const cols = String.fromCharCode(64 + head.length);
  let v = await api.get(`'${tab}'!A1:${cols}`).catch(() => null);
  if (!v) await api.addSheet(tab);
  if (!v?.length) { await api.update([{ range: `'${tab}'!A1:${cols}1`, values: [head] }]); v = [head]; }
  return v;
}

/**
 * Salda: copia las filas a «Histórico» (con la fecha y la operación), apunta el saldo en «Saldos» y quita esas filas de la
 * pestaña principal (las cabeceras y fórmulas de arriba no se tocan). Seguro al reintentar: la operación queda en las copias;
 * se borran solo filas iguales a las copiadas que sigan allí (lo apuntado mientras tanto se queda).
 */
export async function settleTricount(api: SheetsApi, s: Omit<Settlement, 'row' | 'count' | 'total' | 'since' | 'until'>): Promise<Settlement> {
  const main = parseTricountRows(await api.get(`${TRICOUNT.main}!B${TRICOUNT.firstRow}:F`));
  const hist = await ensureTab(api, TRICOUNT.history, HIST_HEAD);
  const saldos = await ensureTab(api, TRICOUNT.settlements, SALDOS_HEAD);
  let copied = parseTricountRows(hist.slice(1).filter(r => text(r?.[6]) === s.id).map(r => r.slice(0, 5)));
  if (!copied.length && main.length) {
    const row = hist.length + 1;
    await api.update([{ range: `'${TRICOUNT.history}'!A${row}:G${row + main.length - 1}`,
      values: main.map(x => [x.date, `'${cleanConcept(x.concept)}`, x.amount, x.payer, x.origin, s.date, s.id]) }], 'USER_ENTERED');
    copied = main;
  }
  const dates = copied.map(x => x.date).sort();
  const out: Settlement = { ...s, since: dates[0] ?? '', until: dates[dates.length - 1] ?? '', count: copied.length,
    total: Math.round(copied.reduce((t, x) => t + x.amount, 0) * 100) / 100, row: 0 };
  const already = saldos.findIndex((r, i) => i > 0 && text(r?.[8]) === s.id);
  if (already < 0) {
    const row = saldos.length + 1;
    await api.update([{ range: `'${TRICOUNT.settlements}'!A${row}:K${row}`,
      values: [[s.date, s.payer, s.receiver, s.amount, out.since, out.until, out.count, out.total, s.id, s.movementId, `'${cleanConcept(s.note)}`]] }], 'USER_ENTERED');
    out.row = row;
  } else out.row = already + 1;
  // Quitar de la principal las filas copiadas que sigan allí (cada copia quita una sola fila igual)
  const key = (x: TricountRow) => `${x.date}|${x.concept}|${x.amount}|${x.payer}|${x.origin}`;
  const pending = new Map<string, number>();
  for (const x of copied) pending.set(key(x), (pending.get(key(x)) ?? 0) + 1);
  const now = parseTricountRows(await api.get(`${TRICOUNT.main}!B${TRICOUNT.firstRow}:F`));
  const del = now.filter(x => { const n = pending.get(key(x)) ?? 0; if (!n) return false; pending.set(key(x), n - 1); return true; });
  if (del.length) await api.deleteRows(del.map(x => ({ tab: TRICOUNT.main, row: x.row })));
  return out;
}

/** Saldo de antes de usar la app (sin el detalle de sus gastos): solo se apunta en «Saldos». */
export async function recordSettlement(api: SheetsApi, s: Omit<Settlement, 'row'>): Promise<void> {
  const saldos = await ensureTab(api, TRICOUNT.settlements, SALDOS_HEAD);
  if (saldos.some((r, i) => i > 0 && text(r?.[8]) === s.id)) return;
  const row = saldos.length + 1;
  await api.update([{ range: `'${TRICOUNT.settlements}'!A${row}:K${row}`,
    values: [[s.date, s.payer, s.receiver, s.amount, s.since, s.until, s.count || '', s.total || '', s.id, s.movementId, `'${cleanConcept(s.note)}`]] }], 'USER_ENTERED');
}

/** Pone el movimiento del cobro (o pago) en un saldo que se apuntó antes de que llegara la transferencia. */
export async function linkSettlement(api: SheetsApi, id: string, movementId: string): Promise<void> {
  const saldos = await api.get(`'${TRICOUNT.settlements}'!A1:K`);
  const i = saldos.findIndex((r, n) => n > 0 && text(r?.[8]) === id);
  if (i > 0) await api.update([{ range: `'${TRICOUNT.settlements}'!J${i + 1}`, values: [[movementId]] }]);
}

/** Saldos anteriores (más recientes primero) y las filas de cada uno en «Histórico». */
export async function readSettlements(api: SheetsApi): Promise<{ settlements: Settlement[]; history: Array<TricountRow & { op: string }> }> {
  const [s, h] = await Promise.all([api.get(`'${TRICOUNT.settlements}'!A1:K`).catch(() => []), api.get(`'${TRICOUNT.history}'!A1:G`).catch(() => [])]);
  const settlements = s.slice(1).flatMap((r, i): Settlement[] => {
    const date = cellDate(r?.[0]), amount = num(r?.[3]);
    return date && amount !== null ? [{ id: text(r?.[8]), date, payer: text(r?.[1]), receiver: text(r?.[2]), amount, since: cellDate(r?.[4]), until: cellDate(r?.[5]),
      count: num(r?.[6]) ?? 0, total: num(r?.[7]) ?? 0, movementId: text(r?.[9]), note: text(r?.[10]), row: i + 2 }] : [];
  }).sort((a, b) => b.date.localeCompare(a.date));
  const history = h.slice(1).flatMap((r, i) => {
    const date = cellDate(r?.[0]), amount = num(r?.[2]);
    return date && amount !== null ? [{ date, concept: text(r?.[1]), amount, payer: text(r?.[3]), origin: text(r?.[4]), row: i + 2, op: text(r?.[6]) }] : [];
  });
  return { settlements, history };
}

/** Quién debe a quién (C2 Pilar, C3 Raul, reparto 50/50). */
export async function tricountBalance(api: SheetsApi): Promise<{ payer: string; receiver: string; amount: number }> {
  const v = await api.get(`${TRICOUNT.main}!C2:C3`);
  const pilar = num(v[0]?.[0]) ?? 0, raul = num(v[1]?.[0]) ?? 0;
  const diff = Math.round((pilar - raul) * 100) / 100;
  if (!diff) return { payer: '', receiver: '', amount: 0 };
  return { payer: diff > 0 ? 'Raul' : 'Pilar', receiver: diff > 0 ? 'Pilar' : 'Raul', amount: Math.round(Math.abs(diff) / 2 * 100) / 100 };
}

/** Comprueba que el fichero elegido tiene el formato de Tricount (pestañas y títulos). */
export async function checkTricountBook(api: SheetsApi): Promise<string> {
  try {
    const [h, d] = await Promise.all([api.get(`${TRICOUNT.main}!B5:F5`), api.get(`${TRICOUNT.dup}!B5:F5`)]);
    if (text(h[0]?.[0]).toLowerCase() !== 'fecha') return 'La pestaña Tricount no tiene el título "Fecha" en B5';
    void d;
    return '';
  } catch (err) {
    return `No es un libro de Tricount (${(err as Error).message})`;
  }
}
